import React, { useEffect, useRef } from "react";
import {
  Home,
  Building2,
  ShoppingBag,
  Briefcase,
  MapPin,
  Clock,
  Car,
  Shield,
  Zap,
  Wind,
  Coffee,
  Phone,
  Mail,
  Facebook,
  Instagram,
  Linkedin,
  Youtube,
  ChevronRight,
  CheckCircle,
  Star,
  ArrowRight,
  Layers,
  Trees,
  Wifi,
  ThermometerSun,
  Dumbbell,
  ParkingCircle,
  SwatchBook,
  Sparkles,
  Heart,
  Users,
  Award,
  MessageCircle
} from "lucide-react";

const UrbanComplexLanding = () => {
  // Animation on scroll effect
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("animate-fadeInUp");
          }
        });
      },
      { threshold: 0.1 },
    );

    document
      .querySelectorAll(".animate-on-scroll")
      .forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div className="font-['Poppins',system-ui,-apple-system,BlinkMacSystemFont,'Segoe_UI',Roboto,'Helvetica_Neue',sans-serif] bg-white text-gray-800 overflow-x-hidden">
      {/* Hero Section */}
      <section
        id="home"
        className="relative min-h-screen flex items-center justify-center pt-20 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900"
      >
        <div className="absolute inset-0 bg-black/50 z-10"></div>
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80')",
          }}
        ></div>
        <div className="relative z-20 text-center text-white px-6 max-w-5xl mx-auto animate-fadeInUp">
          <h1 className="text-5xl md:text-7xl font-bold mb-6 tracking-tight">
            URBAN COMPLEX
          </h1>
          <p className="text-2xl md:text-3xl font-light mb-4">
            Integrated Living, Working & Shopping Spaces
          </p>
          <p className="text-xl md:text-2xl italic text-amber-300 mb-8">
            driven by the philosophy of "Sada Sukhi Raho"
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href="https://wa.me/923487172565?text=I'm%20interested%20in%20investing%20in%20Urban%20Heights"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-green-600 hover:bg-green-700 text-white px-8 py-3 rounded-full font-semibold transition transform hover:scale-105 shadow-xl flex items-center gap-2"
            >
              <MessageCircle size={18} />
              Book Apartments
              <ArrowRight size={18} />
            </a>
          </div>
        </div>
        <div className="absolute bottom-10 left-0 right-0 text-center text-white/70 text-sm flex justify-center gap-6">
          <div className="flex items-center gap-1">
            <CheckCircle size={14} /> 2 Kanal Area
          </div>
          <div className="flex items-center gap-1">
            <CheckCircle size={14} /> 7 Floors
          </div>
          <div className="flex items-center gap-1">
            <CheckCircle size={14} /> Prime Location
          </div>
        </div>
      </section>

      {/* Management Section */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16 animate-on-scroll opacity-0">
            <h2 className="text-4xl font-bold text-gray-800 mb-4">
              The Management
            </h2>
            <div className="w-24 h-1 bg-amber-600 mx-auto mb-6"></div>
            <p className="text-gray-600 max-w-3xl mx-auto">
              We strive in ensuring full satisfaction of our clients. Starting
              the project right from beginning and getting into final shape
              within prescribed time and delivering it to our valued clients.
              The fulfillment of our promises with an atmosphere of trust and
              confidence which we cherish to make permanent features of our
              carrier according to the vision of Ch. Abdul Majeed sahib, a name
              of trust and reliability in real estate sector.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-12 items-center animate-on-scroll opacity-0">
            <div className="bg-gray-50 p-8 rounded-2xl shadow-lg">
              <h3 className="text-2xl font-bold text-amber-700 mb-2">
                Muhammad Shahid Omer
              </h3>
              <p className="text-gray-500 mb-4">Managing Director</p>
              <p className="text-gray-600">
                Urban Complex is a joint venture of Deen Associates & Kohsar
                Builders who are well reputed builders and registered
                consultants of MPCHS & Faisal Town Pvt Ltd. and are working
                since 2004 under the umbrella of Chaudhary Abdul Majeed, CEO
                Zedem International & Faisal Town.
              </p>
            </div>
            <div className="bg-gray-50 p-8 rounded-2xl shadow-lg">
              <h3 className="text-2xl font-bold text-amber-700 mb-2">
                Muhammad Raees Abbasi
              </h3>
              <p className="text-gray-500 mb-4">Project Director</p>
              <p className="text-gray-600">
                Our success depends on our entire work and we are sure to get it
                done. With years of experience and dedication, we ensure every
                project meets the highest standards of quality and timeliness.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Builder Section */}
      <section className="py-20 px-6 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16 animate-on-scroll opacity-0">
            <h2 className="text-4xl font-bold text-gray-800 mb-4">
              The Builder
            </h2>
            <div className="w-24 h-1 bg-amber-600 mx-auto mb-6"></div>
          </div>
          <div className="grid md:grid-cols-2 gap-12 items-center animate-on-scroll opacity-0">
            <div>
              <img
                src="https://images.unsplash.com/photo-1541888946425-d81bb19240f5?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80"
                alt="Modern Building Construction"
                className="rounded-2xl shadow-xl w-full h-80 object-cover"
              />
            </div>
            <div>
              <p className="text-gray-700 leading-relaxed text-lg">
                Deen Associates approach is always focused on shaping a solution
                around our client's drivers and the project requirements. We
                always define the skylines of cities and the quality of living
                and working environments. Our work continues to evolve with the
                changing aspirations and challenges of developers, owners and
                occupants. We routinely design buildings that are adaptable to
                future tenant needs and changing legislative requirements. This
                ensures our client's safely invest in appropriate and long-term
                profitable building stock.
              </p>
              <p className="text-gray-700 leading-relaxed text-lg mt-4">
                We can bring together multiple professional disciplines or work
                with a wider team to achieve excellence and overcome even the
                most complex challenges.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Project Overview */}
      <section id="overview" className="py-20 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16 animate-on-scroll opacity-0">
            <h2 className="text-4xl font-bold text-gray-800 mb-4">
              Project Overview
            </h2>
            <div className="w-24 h-1 bg-amber-600 mx-auto mb-6"></div>
          </div>
          <div className="grid md:grid-cols-2 gap-12 items-center animate-on-scroll opacity-0">
            <div>
              <p className="text-gray-700 leading-relaxed mb-4">
                Urban Complex will be another outstanding project developed by
                Deen Associates and Kohsar Builders. It will offer excellent
                access to the entire city, while providing a refreshing &
                comfort living, enjoyable working environment and energy saving,
                efficient office spaces.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                Comprising of 7 floors building with a total surface of 2 Kanal
                approx, the project is located on the Main Boulevard Corner of
                Block C, Faisal Hills.
              </p>
              <p className="text-gray-700 leading-relaxed">
                The project is designed to provide everything that people need
                to work productively and yet live life to the full. The building
                have generous reception creating an excellent first impression
                to visitors, and all floors enjoy excellent natural light and
                high flexibility for partitioning.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-amber-50 p-6 rounded-xl text-center">
                <Building2 className="w-10 h-10 text-amber-700 mx-auto mb-2" />
                <div className="text-3xl font-bold text-amber-700">7</div>
                <div className="text-gray-600">Floors</div>
              </div>
              <div className="bg-amber-50 p-6 rounded-xl text-center">
                <Layers className="w-10 h-10 text-amber-700 mx-auto mb-2" />
                <div className="text-3xl font-bold text-amber-700">2</div>
                <div className="text-gray-600">Kanal Area</div>
              </div>
              <div className="bg-amber-50 p-6 rounded-xl text-center">
                <Home className="w-10 h-10 text-amber-700 mx-auto mb-2" />
                <div className="text-3xl font-bold text-amber-700">1,2,3</div>
                <div className="text-gray-600">Bedroom Apts</div>
              </div>
              <div className="bg-amber-50 p-6 rounded-xl text-center">
                <ShoppingBag className="w-10 h-10 text-amber-700 mx-auto mb-2" />
                <div className="text-3xl font-bold text-amber-700">19</div>
                <div className="text-gray-600">Shops</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Location Section */}
      <section id="location" className="py-20 px-6 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16 animate-on-scroll opacity-0">
            <h2 className="text-4xl font-bold text-gray-800 mb-4">
              Prime Location
            </h2>
            <div className="w-24 h-1 bg-amber-600 mx-auto mb-6"></div>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Located in the heart of Islamabad; one of the fastest growing
              suburbs. Located on the finest location of Faisal Hills-N-5
              Taxila.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6 mb-12 animate-on-scroll opacity-0">
            <div className="bg-white p-5 rounded-xl shadow-md flex items-start gap-4">
              <Clock className="text-amber-700 w-6 h-6 mt-1" />
              <div>
                <h3 className="font-bold">2 Minutes Drive</h3>
                <p className="text-gray-500">from M-1 Motorway</p>
              </div>
            </div>
            <div className="bg-white p-5 rounded-xl shadow-md flex items-start gap-4">
              <Clock className="text-amber-700 w-6 h-6 mt-1" />
              <div>
                <h3 className="font-bold">2 Minutes Drive</h3>
                <p className="text-gray-500">from G.T. Road</p>
              </div>
            </div>
            <div className="bg-white p-5 rounded-xl shadow-md flex items-start gap-4">
              <Clock className="text-amber-700 w-6 h-6 mt-1" />
              <div>
                <h3 className="font-bold">5 Minutes Drive</h3>
                <p className="text-gray-500">from Margalla Avenue</p>
              </div>
            </div>
            <div className="bg-white p-5 rounded-xl shadow-md flex items-start gap-4">
              <Clock className="text-amber-700 w-6 h-6 mt-1" />
              <div>
                <h3 className="font-bold">10 Minutes Drive</h3>
                <p className="text-gray-500">from Islamabad Airport</p>
              </div>
            </div>
            <div className="bg-white p-5 rounded-xl shadow-md flex items-start gap-4">
              <Clock className="text-amber-700 w-6 h-6 mt-1" />
              <div>
                <h3 className="font-bold">5 Minutes Drive</h3>
                <p className="text-gray-500">from CPEC Route</p>
              </div>
            </div>
            <div className="bg-white p-5 rounded-xl shadow-md flex items-start gap-4">
              <Clock className="text-amber-700 w-6 h-6 mt-1" />
              <div>
                <h3 className="font-bold">25 Minutes Drive</h3>
                <p className="text-gray-500">from Faisal Masjid</p>
              </div>
            </div>
          </div>
          <div className="bg-gradient-to-r from-amber-800 to-amber-600 rounded-2xl p-8 text-white animate-on-scroll opacity-0">
            <h3 className="text-2xl font-bold mb-3">
              Margalla Avenue Islamabad
            </h3>
            <p className="mb-4">
              A Game Changing Scenic Avenue Ready to Change the Urban Dynamics
            </p>
            <p className="text-amber-100">
              The scenic avenue with a stunning backdrop of Margalla Hills
              reduces travelling time from Faisal Mosque to B-17 to 15 mins only
              & reduces distance of D12 from Faisal Hills in just 10 minutes.
            </p>
          </div>
        </div>
      </section>

      {/* Amenities & Specs */}
      <section id="amenities" className="py-20 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16 animate-on-scroll opacity-0">
            <h2 className="text-4xl font-bold text-gray-800 mb-4">
              Luxury Amenities
            </h2>
            <div className="w-24 h-1 bg-amber-600 mx-auto mb-6"></div>
          </div>
          <div className="grid md:grid-cols-3 gap-8 mb-12">
            <div className="bg-gray-50 p-6 rounded-xl text-center animate-on-scroll opacity-0">
              <ParkingCircle className="w-12 h-12 text-amber-700 mx-auto mb-3" />
              <h3 className="text-xl font-bold mb-2">Ample Parking</h3>
              <p className="text-gray-600">
                Open parking space with 24/7 security
              </p>
            </div>
            <div className="bg-gray-50 p-6 rounded-xl text-center animate-on-scroll opacity-0">
              <Zap className="w-12 h-12 text-amber-700 mx-auto mb-3" />
              <h3 className="text-xl font-bold mb-2">High-Speed Lifts</h3>
              <p className="text-gray-600">
                Service lifts for quick vertical transport
              </p>
            </div>
            <div className="bg-gray-50 p-6 rounded-xl text-center animate-on-scroll opacity-0">
              <Shield className="w-12 h-12 text-amber-700 mx-auto mb-3" />
              <h3 className="text-xl font-bold mb-2">Fire Safety System</h3>
              <p className="text-gray-600">
                International fire prevention system with smoke detectors
              </p>
            </div>
            <div className="bg-gray-50 p-6 rounded-xl text-center animate-on-scroll opacity-0">
              <Wind className="w-12 h-12 text-amber-700 mx-auto mb-3" />
              <h3 className="text-xl font-bold mb-2">Earthquake Resistant</h3>
              <p className="text-gray-600">
                RCC frame structure with high durability
              </p>
            </div>
            <div className="bg-gray-50 p-6 rounded-xl text-center animate-on-scroll opacity-0">
              <ThermometerSun className="w-12 h-12 text-amber-700 mx-auto mb-3" />
              <h3 className="text-xl font-bold mb-2">Generator Backup</h3>
              <p className="text-gray-600">
                For common areas and service lifts
              </p>
            </div>
            <div className="bg-gray-50 p-6 rounded-xl text-center animate-on-scroll opacity-0">
              <SwatchBook className="w-12 h-12 text-amber-700 mx-auto mb-3" />
              <h3 className="text-xl font-bold mb-2">Branded Fixtures</h3>
              <p className="text-gray-600">
                Sanitary fittings and modular electrical switches
              </p>
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-8 animate-on-scroll opacity-0">
            <div className="bg-amber-50 p-8 rounded-xl">
              <h3 className="text-2xl font-bold mb-4 text-amber-800">
                General Specifications
              </h3>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-center gap-2">
                  <CheckCircle size={18} className="text-amber-600" />{" "}
                  Earthquake resistant RCC frame structure
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle size={18} className="text-amber-600" /> Internal
                  and external walls as per structural requirement
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle size={18} className="text-amber-600" /> Main
                  entrance & bedroom flush doors
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle size={18} className="text-amber-600" /> Sliding
                  windows with exhaust fans
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle size={18} className="text-amber-600" /> Branded
                  sanitary fixtures in all bathrooms
                </li>
              </ul>
            </div>
            <div className="bg-amber-50 p-8 rounded-xl">
              <h3 className="text-2xl font-bold mb-4 text-amber-800">
                Electrical & Safety
              </h3>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-center gap-2">
                  <CheckCircle size={18} className="text-amber-600" /> Branded
                  electrical modular switches
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle size={18} className="text-amber-600" /> Fire
                  resistant electrical wires
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle size={18} className="text-amber-600" /> Earth
                  leakage circuit breaker per apartment
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle size={18} className="text-amber-600" /> TV, AC &
                  Telephone points in all rooms
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle size={18} className="text-amber-600" />{" "}
                  Firefighting system & smoke detectors
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Commercial & Marketplace */}
      <section className="py-20 px-6 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16 animate-on-scroll opacity-0">
            <h2 className="text-4xl font-bold text-gray-800 mb-4">
              Market Place For World Class Business
            </h2>
            <div className="w-24 h-1 bg-amber-600 mx-auto mb-6"></div>
            <p className="text-gray-600 max-w-2xl mx-auto">
              The Finest Corporate Location with 19 commercial shops & 12
              corporate offices
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-8 mb-12 animate-on-scroll opacity-0">
            <div className="bg-white rounded-2xl overflow-hidden shadow-lg">
              <img
                src="https://images.unsplash.com/photo-1441986300917-64674bd600d8?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80"
                alt="Commercial Shops"
                className="w-full h-64 object-cover"
              />
              <div className="p-6">
                <h3 className="text-2xl font-bold">19 Commercial Shops</h3>
                <p className="text-gray-600 mt-2">
                  Aesthetically designed shops offering great visibility and
                  increased footfalls. Ideal for restaurants, supermarkets, car
                  showrooms, gyms, apparel stores, and medical stores.
                </p>
              </div>
            </div>
            <div className="bg-white rounded-2xl overflow-hidden shadow-lg">
              <img
                src="https://images.unsplash.com/photo-1497366754035-f200968a6e72?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80"
                alt="Corporate Offices"
                className="w-full h-64 object-cover"
              />
              <div className="p-6">
                <h3 className="text-2xl font-bold">12 Corporate Offices</h3>
                <p className="text-gray-600 mt-2">
                  Smart-sized office spaces with iconic architecture, functional
                  design, and world-class luxuries. Your productivity will rise
                  and business image will soar into the elite league.
                </p>
              </div>
            </div>
          </div>
          <div className="bg-amber-700 text-white p-8 rounded-2xl text-center animate-on-scroll opacity-0">
            <h3 className="text-2xl font-bold mb-2">Ali Medical Complex</h3>
            <p className="text-amber-100">
              World-class health facility planning to build international
              standard hospital in Faisal Hills Block-C
            </p>
          </div>
        </div>
      </section>

      {/* Luxury Apartments Section */}
      <section id="apartments" className="py-20 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16 animate-on-scroll opacity-0">
            <h2 className="text-4xl font-bold text-gray-800 mb-4">
              Luxury Living Redefined
            </h2>
            <div className="w-24 h-1 bg-amber-600 mx-auto mb-6"></div>
            <p className="text-gray-600 max-w-2xl mx-auto">
              1, 2 & 3 Bedroom Luxury Apartments - Where every home becomes an
              experience
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 mb-12">
            <div className="bg-gray-50 rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition animate-on-scroll opacity-0">
              <img
                src="https://images.unsplash.com/photo-1560448205-4d9b3e6bb6db?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80"
                alt="1 Bedroom"
                className="w-full h-56 object-cover"
              />
              <div className="p-6">
                <h3 className="text-2xl font-bold text-amber-700">1 Bedroom</h3>
                <p className="text-gray-500 mt-2">
                  Compact luxury with thoughtful design, ample natural light,
                  and premium finishes.
                </p>
                <button className="mt-4 text-amber-700 font-semibold flex items-center gap-1">
                  View Details <ChevronRight size={16} />
                </button>
              </div>
            </div>
            <div className="bg-gray-50 rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition animate-on-scroll opacity-0">
              <img
                src="https://images.unsplash.com/photo-1580587771525-78b9dba3b914?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80"
                alt="2 Bedroom"
                className="w-full h-56 object-cover"
              />
              <div className="p-6">
                <h3 className="text-2xl font-bold text-amber-700">2 Bedroom</h3>
                <p className="text-gray-500 mt-2">
                  Spacious and elegant, perfect for small families with modern
                  amenities.
                </p>
                <button className="mt-4 text-amber-700 font-semibold flex items-center gap-1">
                  View Details <ChevronRight size={16} />
                </button>
              </div>
            </div>
            <div className="bg-gray-50 rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition animate-on-scroll opacity-0">
              <img
                src="https://images.unsplash.com/photo-1618221195710-dd6b41faaea6?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80"
                alt="3 Bedroom"
                className="w-full h-56 object-cover"
              />
              <div className="p-6">
                <h3 className="text-2xl font-bold text-amber-700">3 Bedroom</h3>
                <p className="text-gray-500 mt-2">
                  Ultimate luxury with master suite, large living areas, and
                  stunning views.
                </p>
                <button className="mt-4 text-amber-700 font-semibold flex items-center gap-1">
                  View Details <ChevronRight size={16} />
                </button>
              </div>
            </div>
          </div>
          <div className="bg-gradient-to-r from-gray-800 to-gray-900 text-white p-10 rounded-2xl text-center animate-on-scroll opacity-0">
            <h3 className="text-3xl font-bold mb-4">
              We Make Every Home An Experience
            </h3>
            <p className="text-xl text-gray-300">
              Experience thoughtful design homes that make you fall in love with
              life. Where you can savour a futuristic lifestyle. A home that is
              graced by ample natural light, all day long.
            </p>
          </div>
        </div>
      </section>

      {/* Our Projects */}
      <section className="py-20 px-6 bg-gray-50">
        <div className="max-w-6xl mx-auto text-center animate-on-scroll opacity-0">
          <h2 className="text-4xl font-bold text-gray-800 mb-4">
            Our Legacy of Excellence
          </h2>
          <div className="w-24 h-1 bg-amber-600 mx-auto mb-6"></div>
          <p className="text-gray-600 max-w-3xl mx-auto mb-8">
            We have successfully launched and completed a number of residential
            and commercial projects in Rawalpindi & Islamabad. This has led to
            our well earned reputation as a high quality builder. Our completed
            projects carry a unique blend of elegance, style and comfort and we
            will endeavour to continue our trend of excellence on all future
            projects.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-12">
            <div className="text-center">
              <Award className="w-12 h-12 text-amber-700 mx-auto mb-2" />
              <div className="font-bold text-2xl">15+</div>
              <div className="text-gray-500">Years Experience</div>
            </div>
            <div className="text-center">
              <Users className="w-12 h-12 text-amber-700 mx-auto mb-2" />
              <div className="font-bold text-2xl">1000+</div>
              <div className="text-gray-500">Happy Families</div>
            </div>
            <div className="text-center">
              <Building2 className="w-12 h-12 text-amber-700 mx-auto mb-2" />
              <div className="font-bold text-2xl">25+</div>
              <div className="text-gray-500">Projects Completed</div>
            </div>
            <div className="text-center">
              <Star className="w-12 h-12 text-amber-700 mx-auto mb-2" />
              <div className="font-bold text-2xl">100%</div>
              <div className="text-gray-500">Client Satisfaction</div>
            </div>
          </div>
        </div>
      </section>


      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8 text-center">
        <p>
          © 2025 Urban Complex - A Project of Deen Associates & Kohsar Builders.
          All Rights Reserved.
        </p>
        <p className="text-gray-400 text-sm mt-2">
          Driven by the philosophy of "Sada Sukhi Raho"
        </p>
      </footer>

      {/* Inline animations */}
      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeInUp {
          animation: fadeInUp 0.6s ease-out forwards;
        }
        .animate-on-scroll {
          opacity: 0;
          transform: translateY(30px);
          transition: opacity 0.6s ease-out, transform 0.6s ease-out;
        }
        .animate-on-scroll.animate-fadeInUp {
          opacity: 1;
          transform: translateY(0);
        }
      `}</style>
    </div>
  );
};

export default UrbanComplexLanding;
